import { FormattedMessage } from 'react-intl';

import Button from '../../components/Button/Button';
import SustainableActionCard from '../../components/DetailsCard/components/SustainableActionCard/SustainableActionCard';
import { InfoIcon } from '../../components/Icon/Icon';
import PageLayout from '../../components/Layout/PageLayout/PageLayout';
import { MainTitleStyled } from '../../components/Layout/PageLayout/PageLayout.styled';
import SidebarDetails from '../../components/SidebarDetails/SidebarDetails';
import Tooltip from '../../components/Tooltip/Tooltip';
import { useLocale } from '../../context/LocaleContext';
import useSustainableActions from './hooks/useSustainableActions';
import { BackToCOWrapperStyled } from './SustainableActionsPage.styled';

const SustainableActionsPage: React.FC = () => {
  const { selectedLocale } = useLocale();
  const {
    isLoading,
    sustainableActions,
    selectedSustainableActions,
    carbonOffsets,
    onChangeSustainableAction,
    onPurchase,
    onDeleteCarbonOffsets,
    onGoToCarbonOffsets,
    handleClickBackButton,
  } = useSustainableActions();
  const actionsQty = sustainableActions?.length;
  const isEmptyCardShown = !!carbonOffsets
    ? !carbonOffsets.length && !selectedSustainableActions.length
    : !selectedSustainableActions.length;

  return (
    <PageLayout
      onClickBack={handleClickBackButton}
      isLoading={isLoading}
      isNextPageAvailable={!!selectedSustainableActions.length}
      onClickBreadcrumb={onPurchase}
      backButtonLabel={
        !!carbonOffsets?.length ? (
          <FormattedMessage id="button.backToCO" defaultMessage="Back to Carbon Offset" />
        ) : (
          <FormattedMessage id="button.backToHome" defaultMessage="Back to Home" />
        )
      }
      topSectionContent={
        <>
          <MainTitleStyled level={1}>
            <FormattedMessage id="sustainableActions.title" defaultMessage="Sustainable Actions" />

            {!!carbonOffsets && (
              <Tooltip element={<InfoIcon />}>
                <FormattedMessage
                  id="sustainableActions.tooltip.text"
                  defaultMessage="By choosing this option, you can select to contribute just a Sustainable Action without having to purchase a Carbon Offset Project."
                />
              </Tooltip>
            )}
          </MainTitleStyled>

          <FormattedMessage
            id="select.sustainableAction.text"
            defaultMessage="<p>As an eco-conscious traveler, there is peace of mind knowing you are contributing to sustainable livelihoods globally by supporting projects that promote peace and prosperity for people and the planet.</p> <p>Choose as many Sustainable Actions you wish to contribute to, so you can continue to explore unique and diverse destinations around the globe, responsibly.</p>"
            values={{
              p: (...chunks: string[]) => <p>{chunks}</p>,
            }}
          />

          <p>
            <strong>
              {actionsQty}{' '}
              <FormattedMessage
                id="select.foundActions"
                defaultMessage="Sustainable Actions found"
              />
            </strong>
          </p>
        </>
      }
      topSectionContentRight={
        !!carbonOffsets &&
        !carbonOffsets?.length && (
          <BackToCOWrapperStyled>
            <Button isInverted isFullWidth onClick={onGoToCarbonOffsets}>
              <FormattedMessage id="button.backToCO" defaultMessage="Back to Carbon Offset" />
            </Button>
          </BackToCOWrapperStyled>
        )
      }
      leftSideContent={
        !!actionsQty &&
        sustainableActions.map(
          ({ id, name, type, impact, country, description, projectOwner, ...rest }, index) => (
            <SustainableActionCard
              key={index + id}
              id={id}
              name={name[selectedLocale]}
              type={type[selectedLocale]}
              impact={impact[selectedLocale]}
              country={country[selectedLocale]}
              description={description[selectedLocale]}
              projectOwner={projectOwner[selectedLocale]}
              {...rest}
              onChangeCounter={qty =>
                onChangeSustainableAction({
                  updatedAction: {
                    qty,
                    totalCharge: +rest.pricePerUnit * qty,
                  },
                  selectedId: id,
                })
              }
            />
          )
        )
      }
      rightSideContent={
        <SidebarDetails
          isEmptyCartShown={isEmptyCardShown}
          isEditableSustainableActionsTotalCharge={true}
          selectedSustainableActions={selectedSustainableActions}
          selectedCarbonOffsetProjects={carbonOffsets}
          onDeleteSustainableAction={id =>
            onChangeSustainableAction({ selectedId: id, updatedAction: { qty: 0, totalCharge: 0 } })
          }
          onDeleteCarbonOffset={id => onDeleteCarbonOffsets(id)}
          onClickPurchase={onPurchase}
          onChangeSelectedSustainableActions={(id, updatedAction) =>
            onChangeSustainableAction({ selectedId: id, updatedAction })
          }
        />
      }
    />
  );
};

export default SustainableActionsPage;
